import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Tag, HelpCircle, Save, Trash2, Plus, Edit, LogOut, Activity, RefreshCw, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API } from './Common';

const AdminPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ 
    consultation_price: 400, 
    calendly_url: '', 
    privacy_url: '',
    terms_url: '',
    admin_username: '',
    admin_password: '',
    other_device_flow: 'payment',
    payment_method: 'user_choice'
  });
  const [questions, setQuestions] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newCoupon, setNewCoupon] = useState({ code: '', discount_type: 'percentage', discount_value: 0 });
  
  // Connection logs state
  const [logs, setLogs] = useState([]);
  const [logStats, setLogStats] = useState(null);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilters, setLogFilters] = useState({ method: '', status: '', path: '', ip: '' });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [settingsRes, questionsRes, couponsRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/questions`),
        axios.get(`${API}/coupons`)
      ]);
      setSettings(settingsRes.data);
      setQuestions(questionsRes.data);
      setCoupons(couponsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchLogs = async (page = 0) => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        offset: String(page * 50)
      });
      if (logFilters.method) params.append('method', logFilters.method);
      if (logFilters.status) params.append('status', logFilters.status);
      if (logFilters.path) params.append('path', logFilters.path);
      if (logFilters.ip) params.append('ip', logFilters.ip);
      
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/logs?${params}`),
        axios.get(`${API}/logs/stats`)
      ]);
      
      setLogs(logsRes.data.logs);
      setLogsTotal(logsRes.data.total);
      setLogStats(statsRes.data);
      setLogsPage(page);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Errore nel caricamento dei log');
    } finally {
      setLogsLoading(false);
    }
  };

  const clearOldLogs = async (days = 30) => {
    if (!confirm(`Sei sicuro di voler eliminare i log più vecchi di ${days} giorni?`)) return;
    try {
      const res = await axios.delete(`${API}/logs/clear?days=${days}`);
      toast.success(`Eliminati ${res.data.deleted_count} log`);
      fetchLogs(0);
    } catch (error) {
      toast.error('Errore nell\'eliminazione dei log');
    }
  };

  const updateSettings = async () => {
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const createCoupon = async () => {
    if (!newCoupon.code.trim()) {
      toast.error('Coupon code is required');
      return;
    }
    try {
      await axios.post(`${API}/coupons`, newCoupon);
      toast.success('Coupon created successfully');
      setNewCoupon({ code: '', discount_type: 'percentage', discount_value: 0 });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const deleteCoupon = async (id) => {
    try {
      await axios.delete(`${API}/coupons/${id}`);
      toast.success('Coupon deleted successfully');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const saveQuestion = async () => {
    try {
      if (editingQuestion.id && editingQuestion.id !== 'new') {
        await axios.put(`${API}/questions/${editingQuestion.id}`, editingQuestion);
        toast.success('Question updated successfully');
      } else {
        await axios.post(`${API}/questions`, editingQuestion);
        toast.success('Question created successfully');
      }
      setEditingQuestion(null);
      fetchAll();
    } catch (error) {
      toast.error('Failed to save question');
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      await axios.delete(`${API}/questions/${id}`);
      toast.success('Question deleted successfully');
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin-login');
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto py-12">
        <div className="glass-panel rounded-sm p-8 mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
                <Settings className="w-10 h-10 text-primary" />
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground">Manage questions, coupons, and settings</p>
            </div>
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="outline"
              className="border-secondary text-secondary hover:bg-secondary hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <Tabs defaultValue="settings" className="space-y-6">
          <TabsList className="glass-panel p-1 border border-white/10">
            <TabsTrigger value="settings" data-testid="tab-settings" className="data-[state=active]:bg-primary/20">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="questions" data-testid="tab-questions" className="data-[state=active]:bg-primary/20">
              <HelpCircle className="w-4 h-4 mr-2" />
              Questions
            </TabsTrigger>
            <TabsTrigger value="coupons" data-testid="tab-coupons" className="data-[state=active]:bg-primary/20">
              <Tag className="w-4 h-4 mr-2" />
              Coupons
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs" className="data-[state=active]:bg-primary/20" onClick={() => fetchLogs(0)}>
              <Activity className="w-4 h-4 mr-2" />
              Log Connessioni
            </TabsTrigger>
          </TabsList>

          <TabsContent value="settings">
            <div className="glass-panel rounded-sm p-8">
              <h2 className="text-2xl font-semibold mb-6">General Settings</h2>
              <div className="space-y-6 max-w-2xl">
                <div>
                  <label className="block text-sm mb-2">Consultation Price (€)</label>
                  <Input
                    type="number"
                    value={settings.consultation_price}
                    onChange={(e) => setSettings({ ...settings, consultation_price: parseFloat(e.target.value) })}
                    data-testid="consultation-price-input"
                    className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-4 text-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Calendly URL</label>
                  <Input
                    type="url"
                    value={settings.calendly_url || ''}
                    onChange={(e) => setSettings({ ...settings, calendly_url: e.target.value })}
                    data-testid="calendly-url-input"
                    placeholder="https://calendly.com/your-link"
                    className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-4 text-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Privacy Policy URL</label>
                  <Input
                    type="url"
                    value={settings.privacy_url || ''}
                    onChange={(e) => setSettings({ ...settings, privacy_url: e.target.value })}
                    data-testid="privacy-url-input"
                    placeholder="https://www.smart-info.it/informativa-privacy"
                    className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-4 text-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Termini e Condizioni URL</label>
                  <Input
                    type="url"
                    value={settings.terms_url || ''}
                    onChange={(e) => setSettings({ ...settings, terms_url: e.target.value })}
                    data-testid="terms-url-input"
                    placeholder="https://www.smart-info.it/termini-condizioni"
                    className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-4 text-lg"
                  />
                </div>
                
                <div className="pt-6 border-t border-white/10">
                  <h3 className="text-lg font-semibold mb-4 text-primary">Flusso Utente</h3>
                  <div>
                    <label className="block text-sm mb-3">Dopo il quiz o selezione "Altro", l'utente:</label>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, other_device_flow: 'payment' })}
                        className={`flex-1 p-4 rounded-sm border transition-all ${
                          settings.other_device_flow === 'payment' 
                            ? 'border-primary bg-primary/20 text-primary' 
                            : 'border-white/20 hover:border-primary/50'
                        }`}
                      >
                        <p className="font-semibold mb-1">💳 Pagina Pagamento</p>
                        <p className="text-xs text-muted-foreground">Passa dalla pagina di pagamento (paga ora o dopo)</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, other_device_flow: 'booking' })}
                        className={`flex-1 p-4 rounded-sm border transition-all ${
                          settings.other_device_flow === 'booking' 
                            ? 'border-primary bg-primary/20 text-primary' 
                            : 'border-white/20 hover:border-primary/50'
                        }`}
                      >
                        <p className="font-semibold mb-1">📅 Prenotazione Diretta</p>
                        <p className="text-xs text-muted-foreground">Va direttamente alla prenotazione Calendly</p>
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="pt-6 border-t border-white/10">
                  <h3 className="text-lg font-semibold mb-4 text-primary">Metodo di Pagamento</h3>
                  <div>
                    <label className="block text-sm mb-3">Quale metodo di pagamento mostrare:</label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, payment_method: 'stripe' })}
                        className={`p-4 rounded-sm border transition-all ${
                          settings.payment_method === 'stripe' 
                            ? 'border-primary bg-primary/20 text-primary' 
                            : 'border-white/20 hover:border-primary/50'
                        }`}
                      >
                        <p className="font-semibold mb-1">💳 Solo Stripe</p>
                        <p className="text-xs text-muted-foreground">Carte di credito</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, payment_method: 'paypal' })}
                        className={`p-4 rounded-sm border transition-all ${
                          settings.payment_method === 'paypal' 
                            ? 'border-primary bg-primary/20 text-primary' 
                            : 'border-white/20 hover:border-primary/50'
                        }`}
                      >
                        <p className="font-semibold mb-1">🅿️ Solo PayPal</p>
                        <p className="text-xs text-muted-foreground">Conto PayPal</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSettings({ ...settings, payment_method: 'user_choice' })}
                        className={`p-4 rounded-sm border transition-all ${
                          settings.payment_method === 'user_choice' 
                            ? 'border-primary bg-primary/20 text-primary' 
                            : 'border-white/20 hover:border-primary/50'
                        }`}
                      >
                        <p className="font-semibold mb-1">🔄 Scelta Utente</p>
                        <p className="text-xs text-muted-foreground">Entrambi visibili</p>
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="pt-6 border-t border-white/10">
                  <h3 className="text-lg font-semibold mb-4 text-secondary">Admin Credentials</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm mb-2">Admin Username</label>
                      <Input
                        type="text"
                        value={settings.admin_username || ''}
                        onChange={(e) => setSettings({ ...settings, admin_username: e.target.value })}
                        data-testid="admin-username-input"
                        placeholder="admin"
                        className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-4 text-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2">Admin Password</label>
                      <Input
                        type="password"
                        value={settings.admin_password || ''}
                        onChange={(e) => setSettings({ ...settings, admin_password: e.target.value })}
                        data-testid="admin-password-input"
                        placeholder="••••••••"
                        className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-4 text-lg"
                      />
                      <p className="text-xs text-muted-foreground mt-2">⚠️ Change default password for security</p>
                    </div>
                  </div>
                </div>
                
                <Button
                  onClick={updateSettings}
                  data-testid="save-settings-button"
                  className="rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="questions">
            <div className="glass-panel rounded-sm p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Security Questions</h2>
                <Button
                  onClick={() => setEditingQuestion({
                    id: 'new',
                    order: questions.length + 1,
                    question_en: '',
                    question_it: '',
                    explanation_en: '',
                    explanation_it: '',
                    instructions_ios_en: '',
                    instructions_ios_it: '',
                    instructions_android_en: '',
                    instructions_android_it: ''
                  })}
                  data-testid="add-question-button"
                  className="rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Question
                </Button>
              </div>

              {editingQuestion && (
                <div className="mb-8 p-6 bg-muted/20 rounded-sm border border-primary">
                  <h3 className="text-xl font-semibold mb-4">
                    {editingQuestion.id === 'new' ? 'New Question' : 'Edit Question'}
                  </h3>
                  <div className="space-y-4">
                    <Input
                      type="number"
                      placeholder="Order"
                      value={editingQuestion.order}
                      onChange={(e) => setEditingQuestion({ ...editingQuestion, order: parseInt(e.target.value) })}
                      data-testid="question-order-input"
                      className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-3"
                    />
                    <Input
                      placeholder="Question (English)"
                      value={editingQuestion.question_en}
                      onChange={(e) => setEditingQuestion({ ...editingQuestion, question_en: e.target.value })}
                      data-testid="question-en-input"
                      className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-3"
                    />
                    <Input
                      placeholder="Question (Italian)"
                      value={editingQuestion.question_it}
                      onChange={(e) => setEditingQuestion({ ...editingQuestion, question_it: e.target.value })}
                      data-testid="question-it-input"
                      className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-3"
                    />
                    <textarea
                      placeholder="Explanation (English)"
                      value={editingQuestion.explanation_en}
                      onChange={(e) => setEditingQuestion({ ...editingQuestion, explanation_en: e.target.value })}
                      data-testid="explanation-en-input"
                      className="w-full bg-transparent border border-white/20 focus:border-primary outline-none rounded-sm p-3 min-h-24"
                    />
                    <textarea
                      placeholder="Explanation (Italian)"
                      value={editingQuestion.explanation_it}
                      onChange={(e) => setEditingQuestion({ ...editingQuestion, explanation_it: e.target.value })}
                      data-testid="explanation-it-input"
                      className="w-full bg-transparent border border-white/20 focus:border-primary outline-none rounded-sm p-3 min-h-24"
                    />
                    
                    {/* Device-specific Instructions */}
                    <div className="pt-4 border-t border-white/10">
                      <h4 className="text-lg font-semibold mb-4">Device-Specific Instructions</h4>
                      
                      {/* iOS Instructions */}
                      <div className="mb-6 p-4 bg-primary/5 rounded-sm border border-primary/30">
                        <h5 className="font-semibold mb-3 text-primary">iOS Instructions</h5>
                        <div className="space-y-3">
                          <textarea
                            placeholder="iOS Instructions (English)"
                            value={editingQuestion.instructions_ios_en || ''}
                            onChange={(e) => setEditingQuestion({ ...editingQuestion, instructions_ios_en: e.target.value })}
                            className="w-full bg-transparent border border-white/20 focus:border-primary outline-none rounded-sm p-3 min-h-32"
                          />
                          <textarea
                            placeholder="iOS Instructions (Italian)"
                            value={editingQuestion.instructions_ios_it || ''}
                            onChange={(e) => setEditingQuestion({ ...editingQuestion, instructions_ios_it: e.target.value })}
                            className="w-full bg-transparent border border-white/20 focus:border-primary outline-none rounded-sm p-3 min-h-32"
                          />
                        </div>
                      </div>
                      
                      {/* Android Instructions */}
                      <div className="mb-6 p-4 bg-green-500/5 rounded-sm border border-green-500/30">
                        <h5 className="font-semibold mb-3 text-green-500">Android Instructions</h5>
                        <div className="space-y-3">
                          <textarea
                            placeholder="Android Instructions (English)"
                            value={editingQuestion.instructions_android_en || ''}
                            onChange={(e) => setEditingQuestion({ ...editingQuestion, instructions_android_en: e.target.value })}
                            className="w-full bg-transparent border border-white/20 focus:border-green-500 outline-none rounded-sm p-3 min-h-32"
                          />
                          <textarea
                            placeholder="Android Instructions (Italian)"
                            value={editingQuestion.instructions_android_it || ''}
                            onChange={(e) => setEditingQuestion({ ...editingQuestion, instructions_android_it: e.target.value })}
                            className="w-full bg-transparent border border-white/20 focus:border-green-500 outline-none rounded-sm p-3 min-h-32"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Image Upload Section */}
                    {editingQuestion.id !== 'new' && (
                      <div className="pt-4 border-t border-white/10">
                        <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Images for Instructions (by Device)
                        </h4>
                        
                        {/* iOS Images */}
                        <div className="mb-6 p-4 bg-zinc-900/50 rounded-sm border border-white/10">
                          <h5 className="font-semibold mb-3 text-primary">iOS Screenshots</h5>
                          {editingQuestion.images_ios && editingQuestion.images_ios.length > 0 && (
                            <div className="grid grid-cols-3 gap-3 mb-3">
                              {editingQuestion.images_ios.map((img, idx) => (
                                <div key={idx} className="relative group">
                                  <img 
                                    src={img} 
                                    alt={`iOS ${idx + 1}`}
                                    className="w-full h-32 object-cover rounded border border-primary/30"
                                  />
                                  <button
                                    onClick={async () => {
                                      const filename = img.split('/').pop();
                                      try {
                                        await axios.delete(`${API}/questions/${editingQuestion.id}/images/ios/${filename}`);
                                        toast.success('iOS image deleted');
                                        fetchAll();
                                      } catch (error) {
                                        toast.error('Failed to delete image');
                                      }
                                    }}
                                    className="absolute top-1 right-1 bg-secondary text-white p-1 rounded opacity-0 group-hover:opacity-100 transition"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <input
                            type="file"
                            id={`file-upload-ios-${editingQuestion.id}`}
                            multiple
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const files = Array.from(e.target.files);
                              for (const file of files) {
                                const formData = new FormData();
                                formData.append('file', file);
                                try {
                                  await axios.post(
                                    `${API}/questions/${editingQuestion.id}/upload-image/ios`,
                                    formData,
                                    { headers: { 'Content-Type': 'multipart/form-data' } }
                                  );
                                } catch (error) {
                                  toast.error(`Failed to upload ${file.name}`);
                                }
                              }
                              toast.success('iOS images uploaded');
                              fetchAll();
                              e.target.value = '';
                            }}
                          />
                          <label
                            htmlFor={`file-upload-ios-${editingQuestion.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary text-primary rounded-sm cursor-pointer hover:bg-primary hover:text-black transition text-sm"
                          >
                            <Plus className="w-4 h-4" />
                            Upload iOS Images
                          </label>
                        </div>

                        {/* Android Images */}
                        <div className="mb-6 p-4 bg-zinc-900/50 rounded-sm border border-white/10">
                          <h5 className="font-semibold mb-3 text-green-500">Android Screenshots</h5>
                          {editingQuestion.images_android && editingQuestion.images_android.length > 0 && (
                            <div className="grid grid-cols-3 gap-3 mb-3">
                              {editingQuestion.images_android.map((img, idx) => (
                                <div key={idx} className="relative group">
                                  <img 
                                    src={img} 
                                    alt={`Android ${idx + 1}`}
                                    className="w-full h-32 object-cover rounded border border-green-500/30"
                                  />
                                  <button
                                    onClick={async () => {
                                      const filename = img.split('/').pop();
                                      try {
                                        await axios.delete(`${API}/questions/${editingQuestion.id}/images/android/${filename}`);
                                        toast.success('Android image deleted');
                                        fetchAll();
                                      } catch (error) {
                                        toast.error('Failed to delete image');
                                      }
                                    }}
                                    className="absolute top-1 right-1 bg-secondary text-white p-1 rounded opacity-0 group-hover:opacity-100 transition"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <input
                            type="file"
                            id={`file-upload-android-${editingQuestion.id}`}
                            multiple
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const files = Array.from(e.target.files);
                              for (const file of files) {
                                const formData = new FormData();
                                formData.append('file', file);
                                try {
                                  await axios.post(
                                    `${API}/questions/${editingQuestion.id}/upload-image/android`,
                                    formData,
                                    { headers: { 'Content-Type': 'multipart/form-data' } }
                                  );
                                } catch (error) {
                                  toast.error(`Failed to upload ${file.name}`);
                                }
                              }
                              toast.success('Android images uploaded');
                              fetchAll();
                              e.target.value = '';
                            }}
                          />
                          <label
                            htmlFor={`file-upload-android-${editingQuestion.id}`}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500 text-green-500 rounded-sm cursor-pointer hover:bg-green-500 hover:text-black transition text-sm"
                          >
                            <Plus className="w-4 h-4" />
                            Upload Android Images
                          </label>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          📱 Upload screenshots specific to each operating system
                        </p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={saveQuestion}
                        data-testid="save-question-button"
                        className="rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-xs"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setEditingQuestion(null)}
                        data-testid="cancel-edit-button"
                        className="rounded-none border-white/20 hover:border-secondary/50 uppercase tracking-widest font-mono text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {questions.map((q) => (
                  <div key={q.id} className="p-4 bg-zinc-900/50 rounded-sm border border-white/5 hover:border-primary/30 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-mono text-xs text-muted-foreground mb-1">Order: {q.order}</p>
                        <p className="text-lg font-semibold mb-1">{q.question_en}</p>
                        <p className="text-sm text-muted-foreground">{q.question_it}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingQuestion(q)}
                          data-testid={`edit-question-${q.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteQuestion(q.id)}
                          data-testid={`delete-question-${q.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-secondary" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="coupons">
            <div className="glass-panel rounded-sm p-8">
              <h2 className="text-2xl font-semibold mb-6">Discount Coupons</h2>
              
              <div className="mb-8 p-6 bg-muted/20 rounded-sm border border-primary">
                <h3 className="text-xl font-semibold mb-4">Create New Coupon</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input
                    placeholder="CODE"
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                    data-testid="new-coupon-code-input"
                    className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-3"
                  />
                  <select
                    value={newCoupon.discount_type}
                    onChange={(e) => setNewCoupon({ ...newCoupon, discount_type: e.target.value })}
                    data-testid="new-coupon-type-select"
                    className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-3"
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                  <Input
                    type="number"
                    placeholder="Value"
                    value={newCoupon.discount_value}
                    onChange={(e) => setNewCoupon({ ...newCoupon, discount_value: parseFloat(e.target.value) })}
                    data-testid="new-coupon-value-input"
                    className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-3"
                  />
                  <Button
                    onClick={createCoupon}
                    data-testid="create-coupon-button"
                    className="rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {coupons.map((coupon) => (
                  <div key={coupon.id} className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-sm border border-white/5">
                    <div>
                      <p className="text-lg font-mono font-semibold text-primary">{coupon.code}</p>
                      <p className="text-sm text-muted-foreground">
                        {coupon.discount_type === 'percentage'
                          ? `${coupon.discount_value}% discount`
                          : `€${coupon.discount_value} discount`}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteCoupon(coupon.id)}
                      data-testid={`delete-coupon-${coupon.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-secondary" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Connection Logs Tab */}
          <TabsContent value="logs">
            <div className="glass-panel rounded-sm p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold">Log Connessioni</h2>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => fetchLogs(logsPage)} 
                    variant="outline" 
                    size="sm"
                    disabled={logsLoading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${logsLoading ? 'animate-spin' : ''}`} />
                    Aggiorna
                  </Button>
                  <Button 
                    onClick={() => clearOldLogs(30)} 
                    variant="outline" 
                    size="sm"
                    className="text-red-400 border-red-400/50 hover:bg-red-400/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Pulisci (30gg)
                  </Button>
                </div>
              </div>

              {/* Stats Summary */}
              {logStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-primary/10 border border-primary/30 rounded-sm p-4">
                    <p className="text-sm text-muted-foreground">Totale Richieste</p>
                    <p className="text-2xl font-bold text-primary">{logStats.total_requests?.toLocaleString()}</p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-4">
                    <p className="text-sm text-muted-foreground">Tempo Medio</p>
                    <p className="text-2xl font-bold text-green-400">{logStats.avg_response_ms?.toFixed(0)}ms</p>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-sm p-4">
                    <p className="text-sm text-muted-foreground">Tasso Errori</p>
                    <p className="text-2xl font-bold text-red-400">{logStats.error_rate?.toFixed(1)}%</p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4">
                    <p className="text-sm text-muted-foreground">IP Unici</p>
                    <p className="text-2xl font-bold text-blue-400">{logStats.top_ips?.length || 0}</p>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="flex flex-wrap gap-2 mb-4">
                <Input
                  placeholder="Filtra per path..."
                  value={logFilters.path}
                  onChange={(e) => setLogFilters({...logFilters, path: e.target.value})}
                  className="w-40 h-8 text-sm bg-transparent border-white/20"
                />
                <Input
                  placeholder="Filtra per IP..."
                  value={logFilters.ip}
                  onChange={(e) => setLogFilters({...logFilters, ip: e.target.value})}
                  className="w-32 h-8 text-sm bg-transparent border-white/20"
                />
                <select
                  value={logFilters.method}
                  onChange={(e) => setLogFilters({...logFilters, method: e.target.value})}
                  className="h-8 px-2 text-sm bg-transparent border border-white/20 rounded-sm"
                >
                  <option value="">Tutti i metodi</option>
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                </select>
                <select
                  value={logFilters.status}
                  onChange={(e) => setLogFilters({...logFilters, status: e.target.value})}
                  className="h-8 px-2 text-sm bg-transparent border border-white/20 rounded-sm"
                >
                  <option value="">Tutti gli status</option>
                  <option value="200">200 OK</option>
                  <option value="201">201 Created</option>
                  <option value="400">400 Bad Request</option>
                  <option value="401">401 Unauthorized</option>
                  <option value="404">404 Not Found</option>
                  <option value="500">500 Server Error</option>
                </select>
                <Button 
                  onClick={() => fetchLogs(0)} 
                  size="sm"
                  className="h-8"
                >
                  <Filter className="w-3 h-3 mr-1" />
                  Filtra
                </Button>
              </div>

              {/* Logs Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-2 text-muted-foreground">Timestamp</th>
                      <th className="text-left p-2 text-muted-foreground">Metodo</th>
                      <th className="text-left p-2 text-muted-foreground">Path</th>
                      <th className="text-left p-2 text-muted-foreground">IP</th>
                      <th className="text-left p-2 text-muted-foreground">Status</th>
                      <th className="text-left p-2 text-muted-foreground">Durata</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, idx) => (
                      <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-2 font-mono text-xs">
                          {new Date(log.timestamp).toLocaleString('it-IT')}
                        </td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded text-xs font-mono ${
                            log.method === 'GET' ? 'bg-blue-500/20 text-blue-400' :
                            log.method === 'POST' ? 'bg-green-500/20 text-green-400' :
                            log.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
                            log.method === 'DELETE' ? 'bg-red-500/20 text-red-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {log.method}
                          </span>
                        </td>
                        <td className="p-2 font-mono text-xs max-w-xs truncate">{log.path}</td>
                        <td className="p-2 font-mono text-xs">{log.client_ip}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            log.status_code < 300 ? 'bg-green-500/20 text-green-400' :
                            log.status_code < 400 ? 'bg-blue-500/20 text-blue-400' :
                            log.status_code < 500 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {log.status_code}
                          </span>
                        </td>
                        <td className="p-2 font-mono text-xs">{log.duration_ms?.toFixed(0)}ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Mostrando {logs.length} di {logsTotal} log
                </p>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => fetchLogs(logsPage - 1)} 
                    disabled={logsPage === 0 || logsLoading}
                    variant="outline"
                    size="sm"
                  >
                    Precedente
                  </Button>
                  <Button 
                    onClick={() => fetchLogs(logsPage + 1)} 
                    disabled={(logsPage + 1) * 50 >= logsTotal || logsLoading}
                    variant="outline"
                    size="sm"
                  >
                    Successivo
                  </Button>
                </div>
              </div>

              {/* Top IPs */}
              {logStats?.top_ips && logStats.top_ips.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-3">Top IP Addresses</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {logStats.top_ips.map((item, idx) => (
                      <div key={idx} className="bg-white/5 border border-white/10 rounded-sm p-2 text-center">
                        <p className="font-mono text-xs truncate">{item.ip}</p>
                        <p className="text-primary font-bold">{item.count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;
